import { useEffect, useState } from "react";

/** Pretty countdown: "2d 04h 13m 21s" or "00:14:32" or "Due now". */
export function formatCountdown(targetIso) {
  if (!targetIso) return "—";
  const now = Date.now();
  const t = new Date(targetIso).getTime();
  let diff = Math.floor((t - now) / 1000);
  if (diff <= 0) return "Due now";
  const days = Math.floor(diff / 86400); diff -= days * 86400;
  const hours = Math.floor(diff / 3600); diff -= hours * 3600;
  const mins = Math.floor(diff / 60); diff -= mins * 60;
  const secs = diff;
  const pad = (n) => String(n).padStart(2, "0");
  if (days > 0) return `${days}d ${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

/** Live-ticking countdown cell. When `status !== "active"` returns "—" and
 * does NOT start an interval (zero idle CPU). The `compact` variant renders
 * smaller text suitable for stacking inside a table cell. */
export function CountdownCell({ targetIso, status = "active", compact = false, testId }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (status !== "active" || !targetIso) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [targetIso, status]);
  if (status !== "active") {
    return <span className={`text-xs text-[#94A3B8] ${compact ? "" : ""}`}>—</span>;
  }
  const value = formatCountdown(targetIso);
  const isDue = value === "Due now";
  return (
    <span
      data-testid={testId || "countdown-cell"}
      className={`font-mono ${compact ? "text-[11px]" : "text-sm"} ${
        isDue ? "text-[#B45309]" : "text-[#0F172A]"
      }`}
    >
      {value}
    </span>
  );
}

/** Stacked list of "Next: <countdown>" rows for an entity (test or suite),
 * intended to slot inside a table cell beneath the last-run status. Each
 * schedule shows its recurrence type in tiny muted text. */
export function NextRunsList({ schedules = [], testIdPrefix = "next-run" }) {
  if (!schedules.length) return null;
  return (
    <ul className="flex flex-col gap-0.5">
      {schedules.map((s) => (
        <li
          key={s.id}
          className="inline-flex items-center gap-1.5"
          data-testid={`${testIdPrefix}-${s.id}`}
        >
          <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] shrink-0">
            Next
          </span>
          <CountdownCell
            targetIso={s.next_run_at_utc}
            status={s.status}
            compact
            testId={`${testIdPrefix}-cd-${s.id}`}
          />
          <span className="text-[10px] text-[#94A3B8] truncate">
            ({s.recurrence_type})
          </span>
        </li>
      ))}
    </ul>
  );
}
